import React, { useEffect, useMemo, useRef, useState } from "react";
import { Camera, Crosshair, LogOut, Mail, Globe2, MapPin, BadgeCheck } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../contexts/AuthContext";
import { api } from "../api";
import { supabase } from "../lib/supabaseClient";

const GENDERS = [["male", "Male"], ["female", "Female"], ["prefer_not_to_say", "Prefer not to say"]];
const SECTS = [["sunni", "Sunni"], ["shia", "Shia"], ["prefer_not_to_say", "Prefer not to say"]];
const MADHABS = [["hanafi", "Hanafi"], ["maliki", "Maliki"], ["shafii", "Shafi'i"], ["hanbali", "Hanbali"], ["not_specified", "Not applicable"]];
// Languages per spec: English, Chinese, Tamil, Filipino, Thai, Hindi, Bahasa Indonesia, Malay, German
const LANGS = [
  ["en", "English"], ["zh", "Chinese"], ["ta", "Tamil"], ["tl", "Filipino"],
  ["th", "Thai"], ["hi", "Hindi"], ["id", "Bahasa Indonesia"], ["ms", "Malay"], ["de", "German"],
];

const labelOf = (list, val) => (list.find(([v]) => v === val)?.[1]) || (val ? String(val) : "—");

const fmtDate = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString(undefined, { year: "numeric", month: "long" }); }
  catch { return String(iso).slice(0, 10); }
};

const FIELD_TO_API = ["full_name", "madhab", "sect", "gender", "preferred_language", "date_of_birth"];

export default function Profile() {
  const { user, setUser, logout, refresh } = useAuth();
  const [profile, setProfile] = useState(null);  // server snapshot
  const [edit, setEdit] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState(null);
  const [gpsBusy, setGpsBusy] = useState(false);
  const fileRef = useRef(null);

  // Load fresh profile from server on mount
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.post("/profile/get", {});
        if (cancelled) return;
        setProfile(r.data.profile);
        setForm(buildForm(r.data.profile));
      } catch {
        // Fallback to the cached AuthContext user
        if (user && !cancelled) {
          setProfile(buildFromUser(user));
          setForm(buildForm(user));
        }
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const age = useMemo(() => profile?.age ?? computeAge(profile?.date_of_birth), [profile]);

  if (!profile || !form) return <div style={{ padding: 40, color: "var(--dnya-high-2)" }}>Bismillah... loading your profile</div>;

  const startEdit = () => { setForm(buildForm(profile)); setEdit(true); };
  const cancelEdit = () => { setForm(buildForm(profile)); setEdit(false); };

  const saveAll = async () => {
    setBusy(true);
    const changes = FIELD_TO_API.filter((f) => (form[f] ?? "") !== (profile[f] ?? ""));
    if (changes.length === 0) { setBusy(false); setEdit(false); toast("Nothing to save"); return; }
    try {
      for (const field of changes) {
        await api.post("/profile/update", { field_name: field, field_value: form[field] ?? "" });
      }
      // Country + city aren't part of the inline edit flow per spec — handled via GPS button
      // Re-fetch & sync AuthContext
      const r = await api.post("/profile/get", {});
      setProfile(r.data.profile);
      setForm(buildForm(r.data.profile));
      try { await refresh(); } catch { /* noop */ }
      setEdit(false);
      toast.success(`Saved ${changes.length} ${changes.length === 1 ? "change" : "changes"} Alhamdulillah`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save changes");
    } finally { setBusy(false); }
  };

  const updateLocation = (country, city, lat, lng) => {
    return api.post("/profile/location", { country, city, latitude: lat, longitude: lng });
  };

  const requestGPS = () => {
    if (!navigator.geolocation) {
      toast.error("Geolocation not supported in this browser");
      return;
    }
    setGpsBusy(true);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const { latitude, longitude } = pos.coords;
        try {
          // Reverse-geocode via the free Aladhan-friendly Nominatim
          const r = await fetch(`https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}&zoom=10`, {
            headers: { "Accept-Language": "en" },
          });
          const j = await r.json();
          const city = j?.address?.city || j?.address?.town || j?.address?.village || j?.address?.state || "Unknown";
          const country = j?.address?.country || "Unknown";
          await updateLocation(country, city, latitude, longitude);
          const fresh = await api.post("/profile/get", {});
          setProfile(fresh.data.profile);
          // Bust the cached prayer times so the dashboard refreshes
          try { localStorage.removeItem("dnya_prayer_cache"); } catch { /* noop */ }
          toast.success(`Location updated to ${city}, ${country}`);
        } catch (err) {
          toast.error("Could not update location");
          void err;
        } finally { setGpsBusy(false); }
      },
      () => {
        setGpsBusy(false);
        toast("Please update location manually");
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 60000 },
    );
  };

  const openFilePicker = () => fileRef.current?.click();

  const handlePhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Please choose an image file"); return; }
    if (file.size > 5 * 1024 * 1024) { toast.error("Max image size is 5 MB"); return; }
    setBusy(true);
    try {
      // Resolve user id — we don't expose it on the /profile/get payload, so
      // fall back to AuthContext.user.id
      const uid = user?.id;
      if (!uid) throw new Error("No user id");
      const ext = (file.name.split(".").pop() || "jpg").toLowerCase();
      const path = `avatars/${uid}/profile.${ext}`;
      const { error: upErr } = await supabase.storage.from("avatars").upload(path, file, { upsert: true, contentType: file.type });
      if (upErr) throw upErr;
      const { data: pub } = supabase.storage.from("avatars").getPublicUrl(path);
      const url = `${pub.publicUrl}?v=${Date.now()}`;
      await api.post("/profile/update", { field_name: "profile_photo_url", field_value: url });
      setProfile((p) => ({ ...p, profile_photo_url: url }));
      if (user) setUser({ ...user, profile_photo_url: url });
      toast.success("Photo updated Alhamdulillah");
    } catch (err) {
      console.error(err);
      toast.error("Photo upload failed — verify the 'avatars' Supabase Storage bucket exists and is public");
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const photoUrl = profile.profile_photo_url;
  const initials = (profile.full_name || profile.email || "D")
    .split(" ").map((p) => p[0]).slice(0, 2).join("").toUpperCase();

  return (
    <div className="dnya-reveal" style={{ maxWidth: 780, margin: "0 auto" }}>
      <input ref={fileRef} type="file" accept="image/*" onChange={handlePhoto} style={{ display: "none" }} data-testid="profile-photo-input" />

      {/* ---------- Header (avatar + identity) ---------- */}
      <div style={{
        display: "flex", alignItems: "flex-start", gap: 22, padding: 28, marginBottom: 18,
        background: "var(--dnya-card)",
        border: `1px solid var(--dnya-mosaic-1)`,
        borderRadius: 18,
        boxShadow: "0 6px 20px rgba(10,46,10,0.18)",
      }}>
        {/* Left column: avatar + Edit-below-avatar */}
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 10, flexShrink: 0 }}>
          <button
            onClick={openFilePicker}
            data-testid="profile-avatar"
            aria-label="Change profile photo"
            style={{
              position: "relative", width: 120, height: 120, borderRadius: "50%",
              padding: 0, background: "transparent", cursor: "pointer",
              boxShadow: "0 0 0 3px var(--dnya-bg), 0 0 0 5px var(--dnya-high-1), 0 0 24px rgba(206,240,206,0.35)",
              transition: "transform 200ms ease, box-shadow 200ms ease",
              border: 0,
            }}
            onMouseEnter={(e) => { e.currentTarget.style.transform = "scale(1.03)"; }}
            onMouseLeave={(e) => { e.currentTarget.style.transform = "scale(1)"; }}
          >
            {photoUrl ? (
              <img src={photoUrl} alt="" style={{ width: 120, height: 120, borderRadius: "50%", objectFit: "cover", display: "block" }} />
            ) : (
              <div style={{
                width: 120, height: 120, borderRadius: "50%", background: "var(--dnya-mosaic-1)",
                display: "flex", alignItems: "center", justifyContent: "center",
                color: "var(--dnya-high-1)", fontFamily: "Cinzel, serif", fontSize: 38, fontWeight: 700,
              }}>{initials}</div>
            )}
            <span style={{
              position: "absolute", right: 2, bottom: 2, width: 34, height: 34, borderRadius: "50%",
              background: "var(--dnya-mosaic-2)", display: "flex", alignItems: "center", justifyContent: "center",
              color: "#ffffff", border: `2px solid var(--dnya-bg)`,
            }}>
              <Camera size={16} />
            </span>
          </button>

          {/* Edit / Save+Cancel — directly under the avatar, text-only #CEF0CE */}
          {!edit ? (
            <button
              onClick={startEdit}
              data-testid="profile-edit"
              style={editTextBtn}
            >
              Edit
            </button>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 4 }}>
              <button onClick={saveAll} disabled={busy} data-testid="profile-save" style={editTextBtn}>
                {busy ? "Saving..." : "Save"}
              </button>
              <button onClick={cancelEdit} data-testid="profile-cancel" style={{ ...editTextBtn, color: "var(--dnya-high-2)" }}>
                Cancel
              </button>
            </div>
          )}
        </div>

        {/* Right column: identity + clean text list */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <h1
            data-testid="profile-name"
            className="font-dnya"
            style={{ fontFamily: "Cinzel, serif", fontSize: 28, color: "var(--dnya-high-1)", margin: 0, lineHeight: 1.2, letterSpacing: "0.01em" }}
          >
            {profile.full_name || "—"}
          </h1>
          <div style={{ display: "inline-flex", alignItems: "center", gap: 6, color: "var(--dnya-high-2)", fontSize: 13, marginTop: 6 }}>
            <Mail size={13} /> <span data-testid="profile-email">{profile.email}</span>
          </div>

          {/* Clean vertical text list — no borders, no badges, no wrapping */}
          <div style={{ marginTop: 18, display: "flex", flexDirection: "column", gap: 6 }}>
            <InfoRow label="Age" value={age != null ? String(age) : "—"} testId="profile-age" />
            <InfoRow label="Gender" value={labelOf(GENDERS, profile.gender)} testId="profile-badge-gender" />
            <InfoRow label="Sect" value={labelOf(SECTS, profile.sect)} testId="profile-badge-sect" />
            <InfoRow label="Madhab" value={labelOf(MADHABS, profile.madhab)} testId="profile-badge-madhab" />
          </div>
        </div>
      </div>

      {/* ---------- Details card ---------- */}
      <div style={cardStyle} data-testid="profile-details">
        <SectionTitle>Personal</SectionTitle>
        <Field label="Full Name" testId="profile-field-full-name">
          {edit ? <input value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} style={inp} data-testid="profile-input-full-name" />
            : <ReadOnly>{profile.full_name || "—"}</ReadOnly>}
        </Field>
        <div style={twoCol}>
          <Field label="Date of Birth" testId="profile-field-dob">
            {edit ? <input type="date" value={form.date_of_birth || ""} onChange={(e) => setForm({ ...form, date_of_birth: e.target.value })} style={inp} data-testid="profile-input-dob" />
              : <ReadOnly>{profile.date_of_birth || "—"}</ReadOnly>}
          </Field>
          <Field label="Gender" testId="profile-field-gender">
            {edit ? <Select value={form.gender} options={GENDERS} onChange={(v) => setForm({ ...form, gender: v })} testId="profile-input-gender" />
              : <ReadOnly>{labelOf(GENDERS, profile.gender)}</ReadOnly>}
          </Field>
        </div>

        <SectionTitle>Spiritual</SectionTitle>
        <div style={twoCol}>
          <Field label="Sect" testId="profile-field-sect">
            {edit ? <Select value={form.sect} options={SECTS} onChange={(v) => setForm({ ...form, sect: v })} testId="profile-input-sect" />
              : <ReadOnly>{labelOf(SECTS, profile.sect)}</ReadOnly>}
          </Field>
          <Field label="Madhab" hint="Used by Murshid to tailor your guidance" testId="profile-field-madhab">
            {edit ? <Select value={form.madhab} options={MADHABS} onChange={(v) => setForm({ ...form, madhab: v })} testId="profile-input-madhab" />
              : <div style={{ ...readOnlyStyle, display: "inline-flex", alignItems: "center", gap: 8 }}><BadgeCheck size={14} color="var(--text-primary)" /> {labelOf(MADHABS, profile.madhab)}</div>}
          </Field>
        </div>
        <Field label="Preferred Language" testId="profile-field-lang">
          {edit ? <Select value={form.preferred_language} options={LANGS} onChange={(v) => setForm({ ...form, preferred_language: v })} testId="profile-input-lang" />
            : <div style={{ ...readOnlyStyle, display: "inline-flex", alignItems: "center", gap: 8 }}><Globe2 size={14} color="var(--text-primary)" /> {labelOf(LANGS, profile.preferred_language)}</div>}
        </Field>

        <SectionTitle>Location</SectionTitle>
        <div style={{ display: "flex", alignItems: "stretch", gap: 12, flexWrap: "wrap" }}>
          <div style={{ flex: 1, minWidth: 240 }}>
            <Field label="City · Country" testId="profile-field-location">
              <div style={{ ...readOnlyStyle, display: "inline-flex", alignItems: "center", gap: 8 }}>
                <MapPin size={14} color="#CEF0CE" />
                {profile.city || profile.country
                  ? `${profile.city || ""}${profile.city && profile.country ? " · " : ""}${profile.country || ""}`
                  : "Tap the GPS button to set your location"}
              </div>
            </Field>
          </div>
          <button
            onClick={requestGPS}
            disabled={gpsBusy}
            data-testid="profile-gps"
            style={{ ...primaryBtn, alignSelf: "flex-end", marginBottom: 14 }}
            onMouseEnter={(e) => { if (!gpsBusy) e.currentTarget.style.background = "var(--dnya-mosaic-2)"; }}
            onMouseLeave={(e) => { if (!gpsBusy) e.currentTarget.style.background = "var(--dnya-mosaic-1)"; }}
          >
            <Crosshair size={14} /> {gpsBusy ? "Locating..." : "Use GPS"}
          </button>
        </div>

        <SectionTitle>Account</SectionTitle>
        <div style={twoCol}>
          <Field label="Member Since" testId="profile-field-since"><ReadOnly>{fmtDate(profile.created_at)}</ReadOnly></Field>
          <Field label="Google Calendar" testId="profile-field-gcal"><ReadOnly>{profile.google_calendar_connected ? "Connected" : "Not connected"}</ReadOnly></Field>
        </div>
      </div>

      <div style={{ marginTop: 22, display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 12 }}>
        <a
          href="/terms"
          data-testid="profile-terms-link"
          style={{
            color: "var(--dnya-high-1)", fontSize: 13, textDecoration: "underline",
            textUnderlineOffset: 3, cursor: "pointer",
          }}
        >
          Terms &amp; Conditions
        </a>
        <button
          onClick={logout}
          data-testid="profile-logout"
          style={{ ...primaryBtn, background: "transparent", border: `1px solid var(--dnya-high-1)`, color: "var(--dnya-high-1)" }}
          onMouseEnter={(e) => { e.currentTarget.style.background = "var(--dnya-mosaic-1)"; e.currentTarget.style.color = "#ffffff"; }}
          onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; e.currentTarget.style.color = "var(--dnya-high-1)"; }}
        >
          <LogOut size={14} /> Log out
        </button>
      </div>
    </div>
  );
}

/* ---------- Helpers ---------- */

function buildForm(p) {
  return {
    full_name: p.full_name || "",
    date_of_birth: p.date_of_birth || "",
    gender: p.gender || "prefer_not_to_say",
    sect: p.sect || "prefer_not_to_say",
    madhab: p.madhab || "not_specified",
    preferred_language: p.preferred_language || "en",
  };
}

function buildFromUser(u) {
  return {
    full_name: u.full_name, email: u.email, date_of_birth: u.date_of_birth,
    gender: u.gender, sect: u.sect, madhab: u.madhab,
    preferred_language: u.preferred_language,
    country: u.country, city: u.city, latitude: u.latitude, longitude: u.longitude,
    profile_photo_url: u.profile_photo_url, terms_accepted: u.terms_accepted,
    google_calendar_connected: u.google_calendar_connected,
    created_at: u.created_at,
  };
}

function computeAge(dob) {
  if (!dob) return null;
  const d = new Date(dob);
  if (Number.isNaN(d.getTime())) return null;
  const t = new Date();
  let y = t.getFullYear() - d.getFullYear();
  if (t.getMonth() < d.getMonth() || (t.getMonth() === d.getMonth() && t.getDate() < d.getDate())) y -= 1;
  return y >= 0 ? y : null;
}

/* ---------- Small UI primitives (colocated so design is self-contained) ---------- */

const cardStyle = {
  background: "var(--dnya-card)",
  border: `1px solid var(--dnya-mosaic-1)`,
  borderRadius: 18,
  padding: 28,
  boxShadow: "0 6px 20px rgba(10,46,10,0.16)",
};

const inp = {
  width: "100%", padding: "11px 13px", borderRadius: 10,
  border: `1px solid var(--dnya-mosaic-1)`,
  background: "var(--dnya-input-bg)", color: "var(--dnya-text)",
  outline: "none", fontSize: 14, fontFamily: "Inter, sans-serif",
  transition: "border-color 200ms ease, box-shadow 200ms ease",
};

const readOnlyStyle = {
  padding: "11px 13px", borderRadius: 10,
  background: "var(--dnya-bg)", color: "var(--dnya-text)", fontSize: 14,
  border: `1px solid var(--dnya-mosaic-1)`,
};

const twoCol = { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 };

const primaryBtn = {
  display: "inline-flex", alignItems: "center", gap: 6,
  padding: "10px 18px", borderRadius: 10,
  background: "var(--dnya-mosaic-1)", color: "#ffffff",
  border: `1px solid var(--dnya-high-1)`,
  cursor: "pointer", fontWeight: 600, fontSize: 13,
  transition: "background 200ms ease, transform 200ms ease",
  minHeight: 38,
};

// Text-only "Edit" / "Save" / "Cancel" button below the avatar — per spec:
// small, subtle, text only in #CEF0CE, no border, no background.
const editTextBtn = {
  background: "transparent", border: 0, padding: "4px 6px",
  color: "var(--dnya-high-1)", fontSize: 13, fontWeight: 500,
  cursor: "pointer", letterSpacing: "0.04em",
  transition: "opacity 200ms ease",
  minHeight: 32,
};

// Clean label/value row used in the profile header — no borders, no badges,
// label #4a9a4a 11px / value #ffffff 14px, nowrap, min-width 120px.
const InfoRow = ({ label, value, testId }) => (
  <div
    data-testid={testId}
    style={{
      display: "flex", alignItems: "baseline", gap: 14,
      whiteSpace: "nowrap", minWidth: 120,
    }}
  >
    <span style={{
      color: "#4a9a4a", fontSize: 11, fontWeight: 600,
      letterSpacing: "0.08em", textTransform: "uppercase",
      minWidth: 70, display: "inline-block",
    }}>{label}</span>
    <span style={{ color: "var(--text-primary)", fontSize: 14, fontWeight: 500 }}>{value}</span>
  </div>
);

const SectionTitle = ({ children }) => (
  <div style={{
    fontFamily: "Cinzel, serif", color: "var(--dnya-high-1)", fontSize: 13, fontWeight: 600,
    textTransform: "uppercase", letterSpacing: "0.18em", marginTop: 6, marginBottom: 12,
    paddingBottom: 6, borderBottom: `1px solid var(--dnya-mosaic-1)`,
  }}>{children}</div>
);

const Field = ({ label, hint, children, testId }) => (
  <div style={{ marginBottom: 16 }} data-testid={testId}>
    <div style={{
      fontSize: 11, color: "var(--dnya-high-1)", marginBottom: 6,
      fontWeight: 600, letterSpacing: "0.1em", textTransform: "uppercase",
    }}>{label}</div>
    {children}
    {hint && <div style={{ fontSize: 11, color: "var(--dnya-high-2)", marginTop: 5, fontStyle: "italic" }}>{hint}</div>}
  </div>
);

const ReadOnly = ({ children }) => <div style={readOnlyStyle}>{children}</div>;

const Select = ({ value, options, onChange, testId }) => (
  <select value={value} onChange={(e) => onChange(e.target.value)} style={inp} data-testid={testId}>
    {options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
  </select>
);
